import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, LayoutGrid, ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import api from "../../../api/axios";

const SousCategoryPage = () => {
  // Récupération des paramètres : /categorie/:categorie/:sous-categorie
  const { categorie, "sousCategorie": sousCategorieSlug } = useParams(); 
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        // IMPORTANT : L'API doit retourner les produits liés au slug de la sous-catégorie
        const res = await api.get(`/categorie/cat?cat=${sousCategorieSlug}`);
        
        if (res.data.success) {
          // On s'assure de récupérer un tableau de produits
          setProducts(res.data.data || []);
        }
      } catch (err) {
        console.error("Erreur de chargement :", err);
      } finally {
        setLoading(false);
      }
    };

    if (sousCategorieSlug) fetchProducts();
  }, [sousCategorieSlug]);




  

  const ProductCard = ({ product }) => {
    const price = parseFloat(product.price);
    const oldPrice = product.old_price ? parseFloat(product.old_price) : null;
    const discount = (oldPrice && oldPrice > price) 
      ? Math.round(((oldPrice - price) / oldPrice) * 100) 
      : 0;

    const imageSrc = product.main_image?.startsWith('/storage') 
      ? `${BACKEND_BASE}${product.main_image}` 
      : `${BACKEND_BASE}/storage/${product.main_image}`;

    return (
      <Link 
        to={`/product/${product.slug}`} 
        className="group flex flex-col bg-white p-4 transition-all duration-300 hover:shadow-2xl border border-gray-100 rounded-2xl h-full"
      >
        <div className="relative mb-4 h-48 w-full overflow-hidden sm:h-64">
          {discount > 0 && (
            <div className="absolute top-0 left-0 z-10 bg-[#ffaa00] px-2 py-1 text-[10px] font-black text-white uppercase tracking-wider rounded-br-lg">
              -{discount}%
            </div>
          )}
          <img 
            src={imageSrc} 
            alt={`${product.brand} ${product.title}`} 
            className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="flex flex-col text-center mt-auto">
          <span className="text-[10px] font-bold text-[#8A8A89] uppercase tracking-widest mb-1">
            {product.brand || 'Electro Titan'}
          </span>
          <h3 className="font-sans text-sm font-bold text-gray-900 line-clamp-2 min-h-[40px] mb-2 uppercase tracking-tight group-hover:text-[#ffaa00] transition-colors">
            {product.title}
          </h3>
          <div className="flex flex-col items-center gap-1">
            <span className="text-lg font-black text-[#ffaa00]">{product.price} dh</span>
            {product.old_price && (
              <span className="text-xs text-[#8A8A89] line-through font-bold">{product.old_price} dh</span>
            )}
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8">
      {/* --- SEO DYNAMIQUE --- */}
      <Helmet>
        <title>{`${sousCategorieSlug?.toUpperCase()} | ${categorie?.toUpperCase()} Electro Titan Maroc`}</title>
        <meta 
          key="description" name="description" 
          content={`Découvrez notre gamme de ${sousCategorieSlug} au meilleur prix chez Electro Titan. Livraison rapide à Salé, Rabat et partout au Maroc.`} 
        />
        <link rel="canonical" href={window.location.href} />
        
        {/* Schéma CollectionPage pour Google */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": sousCategorieSlug,
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": products.length,
              "itemListElement": products.map((p, i) => ({
                "@type": "ListItem",
                "position": i + 1,
                "url": `https://www.titanelectro.ma/product/${p.slug}`
              }))
            }
          })}
        </script>
      </Helmet>

      <div className="max-w-7xl mx-auto">
        {/* Fil d'Ariane (Breadcrumbs) - Vital pour le SEO Local */}
        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400 mb-6">
          <Link to="/" className="hover:text-black">Accueil</Link>
          <ChevronRight size={12} />
          <Link to={`/categorie/${categorie}`} className="hover:text-black">{categorie}</Link>
          <ChevronRight size={12} />
          <span className="text-[#ffaa00]">{sousCategorieSlug}</span>
        </div>

        <h1 className="text-4xl font-black uppercase tracking-tighter text-gray-900 mb-2">
          {sousCategorieSlug}
        </h1>
        <p className="text-xs font-bold text-[#8A8A89] mb-10">
          {loading ? 'Recherche en cours...' : `${products.length} articles trouvés`}
        </p>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Loader2 className="animate-spin text-[#ffaa00]" size={40} />
            <p className="text-[10px] font-black uppercase text-gray-300 tracking-[0.3em]">Mise à jour du catalogue...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {products.length === 0 && (
              <div className="text-center py-32 border-2 border-dashed border-gray-100 rounded-[3rem]">
                <LayoutGrid className="text-gray-200 mx-auto mb-4" size={48} />
                <h3 className="text-sm font-black uppercase text-gray-900 tracking-widest">Aucun produit disponible</h3>
                <Link to="/" className="inline-block mt-8 text-[10px] font-black uppercase bg-black text-white px-8 py-4 rounded-xl">
                  Retour à l'accueil
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SousCategoryPage;